import { useState, useRef, useEffect } from "react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { ScrollArea } from "./ui/scroll-area";
import { Send } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { QuickStartPills } from "./QuickStartPills";
import { ResumeComparisonView } from "./ResumeComparisonView";
import { CopyableOutputBlock } from "./CopyableOutputBlock";
import { LinkedInSectionBlock } from "./LinkedInSectionBlock";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import ReactMarkdown from "react-markdown";

export const ChatInterface = ({ sessionId, resumeData, sessionType }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [userName, setUserName] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (sessionId) {
      loadMessages();
    }
  }, [sessionId]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const profile = await api.getProfile();
        if (profile?.full_name) {
          const firstName = profile.full_name.split(" ")[0];
          setUserName(firstName);
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };

    fetchUserProfile();
  }, []);

  const loadMessages = async () => {
    if (!sessionId) return;

    try {
      const data = await api.getMessages(sessionId);
      if (data.data.messages?.length > 0) {
        setMessages(
          data.data.messages?.map((msg) => ({ role: msg.role, content: msg.content }))
        );
      } else {
        console.log("No messages found");
        setMessages([]);
      }
    } catch (error) {
      console.error("Error loading messages:", error);
    }
  };

  const saveMessage = async (role, content) => {
    if (!sessionId) return;

    try {
      await api.createMessage(sessionId, role, content );
    } catch (error) {
      console.error("Error saving message:", error);
    }
  };

  const generateSessionTitle = async () => {
    if (!sessionId || messages.length < 2) return;

    try {
      const session = await api.getSession(sessionId);
      if (session?.title) return;

      const firstUserMessage = messages.find((m) => m.role === "user");
      if (!firstUserMessage) return;

      let title = firstUserMessage.content.trim();
      if (title.length > 50) {
        title = title.substring(0, 47) + "...";
      }

      await api.updateSession(sessionId, { title });
    } catch (error) {
      console.error("Error updating session title:", error);
    }
  };

  const streamChat = async (userMessage, overrideSessionType) => {
    const CHAT_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/chat`;

    try {
      const resp = await fetch(CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
          // Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          messages: [...messages, userMessage],
          sessionId,
          resumeData,
          sessionType: overrideSessionType || sessionType,
        }),
      });

      if (!resp.ok) {
        if (resp.status === 429) {
          toast.error("Rate limit exceeded. Please try again in a moment.");
          return;
        }
        if (resp.status === 402) {
          toast.error("AI usage limit reached. Please contact support.");
          return;
        }
        if (resp.status === 401) {
          toast.error("Session expired. Please log in again.");
          console.log("resp", resp);
          // api.redirectToLogin();
          return;
        }
        throw new Error("Failed to start stream");
      }

      if (!resp.body) throw new Error("No response body");

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";
      let assistantContent = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") break;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              assistantContent += content;
              setMessages((prev) => {
                const last = prev[prev.length - 1];
                if (last?.role === "assistant") {
                  return prev.map((m, i) =>
                    i === prev.length - 1
                      ? { ...m, content: assistantContent }
                      : m
                  );
                }
                return [
                  ...prev,
                  { role: "assistant", content: assistantContent },
                ];
              });
            }

            if (parsed.choices?.[0]?.finish_reason === "stop") {
              await saveMessage("assistant", assistantContent);
              // Generate title after first exchange
              if (messages.length === 1) {
                setTimeout(() => generateSessionTitle(), 500);
              }
            }
          } catch {
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }
    } catch (error) {
      console.error("Chat error:", error);
      toast.error("Failed to send message. Please try again.");
    }
  };

  const handleSend = async (messageText, pillSessionType) => {
    const textToSend = messageText || input.trim();
    if (!textToSend || isLoading) return;

    const userMessage = { role: "user", content: textToSend };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    await saveMessage("user", textToSend);
    await streamChat(userMessage, pillSessionType);
    setIsLoading(false);
  };

  const handlePillClick = (prompt, pillSessionType) => {
    handleSend(prompt, pillSessionType);
  };

  // Parse special output formats
  const parseResumeOptimization = (content) => {
    const match = content.match(
      /---RESUME_OPTIMIZATION_START---([\s\S]*?)---RESUME_OPTIMIZATION_END---/
    );
    if (!match) return null;

    const sections = [];
    const sectionTexts = match[1].split("---").filter((s) => s.trim());

    console.log(
      "[🔍 DEBUG] Parsing resume sections:",
      sectionTexts.length,
      "sections found"
    );

    sectionTexts.forEach((sectionText, idx) => {
      console.log(`[🔍 DEBUG] Section ${idx}:`, sectionText.substring(0, 100));

      const titleMatch = sectionText.match(/##\s*(.+)/);
      const contentMatch = sectionText.match(
        /##\s*.+\n([\s\S]*?)###\s*What Changed:/
      );
      const changedMatch = sectionText.match(
        /###\s*What Changed:\n([\s\S]*?)###\s*Why This Works:/
      );
      const whyMatch = sectionText.match(/###\s*Why This Works:\n([\s\S]*?)$/);

      console.log(`[🔍 DEBUG] Matches for section ${idx}:`, {
        title: !!titleMatch,
        content: !!contentMatch,
        changed: !!changedMatch,
        why: !!whyMatch,
      });

      if (titleMatch && contentMatch && changedMatch && whyMatch) {
        sections.push({
          title: titleMatch[1].trim(),
          content: contentMatch[1].trim(),
          whatChanged: changedMatch[1].trim(),
          why: whyMatch[1].trim(),
        });
      } else {
        console.warn(`[⚠️ DEBUG] Section ${idx} failed to match:`, {
          titleMatch: !!titleMatch,
          contentMatch: !!contentMatch,
          changedMatch: !!changedMatch,
          whyMatch: !!whyMatch,
        });
      }
    });

    // CRITICAL FIX: Return null if no valid sections were parsed
    if (sections.length === 0) {
      console.error(
        "[❌ PARSE FAILED] Format markers found but no valid sections parsed"
      );
      return null;
    }

    const fullText = sections
      .map((s) => `${s.title}\n\n${s.content}`)
      .join("\n\n");

    // PART 2 FIX: Check if fullText is empty even if sections exist
    if (fullText.trim() === "") {
      console.error(
        "[❌ PARSE FAILED] Format markers found but content is empty"
      );
      return null;
    }

    return { sections, fullText };
  };

  const parseJobAnalysis = (content) => {
    const match = content.match(
      /---JOB_ANALYSIS_START---([\s\S]*?)---JOB_ANALYSIS_END---/
    );
    if (!match) return null;
    return match[1].trim();
  };

  const parseCoverLetter = (content) => {
    const match = content.match(
      /---COVER_LETTER_START---([\s\S]*?)---COVER_LETTER_END---/
    );
    if (!match) return null;

    const letterText = match[1].trim();
    const whyMatch = content.match(/###\s*Why This Works:\n([\s\S]*?)$/);

    return {
      content: letterText,
      why: whyMatch ? whyMatch[1].trim() : "",
    };
  };

  const parseLinkedInSection = (content) => {
    const headlineMatch = content.match(
      /---LINKEDIN_HEADLINE_START---([\s\S]*?)---LINKEDIN_HEADLINE_END---/
    );
    const aboutMatch = content.match(
      /---LINKEDIN_ABOUT_START---([\s\S]*?)---LINKEDIN_ABOUT_END---/
    );
    const experienceMatch = content.match(
      /---LINKEDIN_EXPERIENCE_START---([\s\S]*?)---LINKEDIN_EXPERIENCE_END---/
    );
    const skillsMatch = content.match(
      /---LINKEDIN_SKILLS_START---([\s\S]*?)---LINKEDIN_SKILLS_END---/
    );

    const sections = [];

    if (headlineMatch) {
      const sectionContent = headlineMatch[1].trim();
      const textMatch = sectionContent.match(
        /^([\s\S]*?)###\s*Why This Works:/
      );
      const whyMatch = sectionContent.match(
        /###\s*Why This Works:\n([\s\S]*?)$/
      );

      sections.push({
        type: "headline",
        title: "LinkedIn Headline",
        description:
          "Your professional headline appears next to your name across LinkedIn",
        content: textMatch ? textMatch[1].trim() : sectionContent,
        why: whyMatch ? whyMatch[1].trim() : "",
        characterLimit: "120 characters max",
      });
    }

    if (aboutMatch) {
      const sectionContent = aboutMatch[1].trim();
      const textMatch = sectionContent.match(
        /^([\s\S]*?)###\s*Why This Works:/
      );
      const whyMatch = sectionContent.match(
        /###\s*Why This Works:\n([\s\S]*?)$/
      );

      sections.push({
        type: "about",
        title: "LinkedIn About Section",
        description: "Your professional story and what makes you unique",
        content: textMatch ? textMatch[1].trim() : sectionContent,
        why: whyMatch ? whyMatch[1].trim() : "",
        characterLimit: "2000 characters max",
      });
    }

    if (experienceMatch) {
      const sectionContent = experienceMatch[1].trim();
      const textMatch = sectionContent.match(
        /^([\s\S]*?)###\s*Why This Works:/
      );
      const whyMatch = sectionContent.match(
        /###\s*Why This Works:\n([\s\S]*?)$/
      );

      sections.push({
        type: "experience",
        title: "LinkedIn Experience Bullets",
        description: "Achievement-focused descriptions for your work history",
        content: textMatch ? textMatch[1].trim() : sectionContent,
        why: whyMatch ? whyMatch[1].trim() : "",
      });
    }

    if (skillsMatch) {
      const sectionContent = skillsMatch[1].trim();
      const textMatch = sectionContent.match(
        /^([\s\S]*?)###\s*Why This Works:/
      );
      const whyMatch = sectionContent.match(
        /###\s*Why This Works:\n([\s\S]*?)$/
      );

      sections.push({
        type: "skills",
        title: "Top LinkedIn Skills",
        description: "Prioritized skills for recruiter searchability",
        content: textMatch ? textMatch[1].trim() : sectionContent,
        why: whyMatch ? whyMatch[1].trim() : "",
      });
    }

    return sections.length > 0 ? sections : null;
  };

  const parseInterviewPrep = (content) => {
    const match = content.match(
      /---INTERVIEW_PREP_START---([\s\S]*?)---INTERVIEW_PREP_END---/
    );
    if (!match) return null;
    return match[1].trim();
  };

  const parsePracticeQuestions = (content) => {
    const match = content.match(
      /---PRACTICE_QUESTIONS_START---([\s\S]*?)---PRACTICE_QUESTIONS_END---/
    );
    if (!match) return null;
    return match[1].trim();
  };

  const parseOutreachTemplates = (content) => {
    const match = content.match(
      /---OUTREACH_TEMPLATES_START---([\s\S]*?)---OUTREACH_TEMPLATES_END---/
    );
    if (!match) return null;

    // Parse individual templates by splitting on ### followed by emoji numbers
    const fullContent = match[1].trim();
    const templates = fullContent.split(/###\s+\d+️⃣/).filter(Boolean);

    return templates.map((template) => {
      const lines = template.split("\n");
      const title = lines[0]?.trim() || "Template";
      const content = lines.slice(1).join("\n").trim();
      return { title, content };
    });
  };

  const renderMessage = (message) => {
    if (message.role === "user") {
      return (
        <p className="whitespace-pre-wrap text-sm leading-relaxed">
          {message.content}
        </p>
      );
    }

    // Check for special formats
    const resumeOptimization = parseResumeOptimization(message.content);
    if (resumeOptimization) {
      console.log("[✅ PARSED] Resume Optimization", resumeOptimization);
      return (
        <ResumeComparisonView
          sections={resumeOptimization.sections}
          fullResumeText={resumeOptimization.fullText}
        />
      );
    }
    if (
      message.content.includes("RESUME_OPTIMIZATION") &&
      !resumeOptimization
    ) {
      console.error(
        "[❌ PARSE FAILED] Resume markers found but parsing failed"
      );
    }

    const jobAnalysis = parseJobAnalysis(message.content);
    if (jobAnalysis) {
      console.log("[✅ PARSED] Job Analysis");
      return (
        <CopyableOutputBlock
          title="Job Description Gap Analysis"
          description="Your competitive advantages and action plan"
          content={jobAnalysis}
          educationalCallout="This analysis helps you position your application strategically by emphasizing your strongest matches and addressing any gaps proactively."
        />
      );
    }
    if (message.content.includes("JOB_ANALYSIS") && !jobAnalysis) {
      console.error(
        "[❌ PARSE FAILED] Job Analysis markers found but parsing failed"
      );
    }

    const coverLetter = parseCoverLetter(message.content);
    if (coverLetter) {
      console.log("[✅ PARSED] Cover Letter");
      return (
        <CopyableOutputBlock
          title="Your Tailored Cover Letter"
          description="Ready to customize and send"
          content={coverLetter.content}
          educationalCallout={coverLetter.why}
        />
      );
    }
    if (message.content.includes("COVER_LETTER") && !coverLetter) {
      console.error(
        "[❌ PARSE FAILED] Cover Letter markers found but parsing failed"
      );
    }

    const linkedInSections = parseLinkedInSection(message.content);
    if (linkedInSections) {
      console.log(
        "[✅ PARSED] LinkedIn Sections",
        linkedInSections.length,
        "sections"
      );
      return (
        <Card className="w-full">
          <CardHeader>
            <CardTitle>LinkedIn Optimized Profile</CardTitle>
            <CardDescription>
              Your professional profile optimized for recruiter visibility and
              networking impact. Copy each section below and paste it into the
              corresponding LinkedIn field.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {linkedInSections.map((section, idx) => (
              <LinkedInSectionBlock
                key={idx}
                sectionTitle={section.title}
                description={section.description}
                content={section.content}
                educationalNote={section.why}
                characterLimit={section.characterLimit}
              />
            ))}
          </CardContent>
        </Card>
      );
    }
    if (message.content.includes("LINKEDIN_") && !linkedInSections) {
      console.error(
        "[❌ PARSE FAILED] LinkedIn markers found but parsing failed"
      );
    }

    const interviewPrep = parseInterviewPrep(message.content);
    if (interviewPrep) {
      console.log("[✅ PARSED] Interview Prep");
      return (
        <CopyableOutputBlock
          title="Interview Preparation Cheatsheet"
          description="Everything you need to prepare for your interview"
          content={interviewPrep}
          educationalCallout="Use this cheatsheet to prepare talking points and practice your STAR examples. The best interviews feel like conversations where you naturally weave in your achievements."
        />
      );
    }
    if (message.content.includes("INTERVIEW_PREP") && !interviewPrep) {
      console.error(
        "[❌ PARSE FAILED] Interview Prep markers found but parsing failed"
      );
    }

    const practiceQuestions = parsePracticeQuestions(message.content);
    if (practiceQuestions) {
      console.log("[✅ PARSED] Practice Questions");
      return (
        <CopyableOutputBlock
          title="Practice Interview Questions"
          description="10 likely questions with answer frameworks"
          content={practiceQuestions}
          educationalCallout="Practice these out loud! The framework gives you structure, but your delivery should feel natural and conversational. Record yourself to identify areas for improvement."
        />
      );
    }
    if (message.content.includes("PRACTICE_QUESTIONS") && !practiceQuestions) {
      console.error(
        "[❌ PARSE FAILED] Practice Questions markers found but parsing failed"
      );
    }

    const outreachTemplates = parseOutreachTemplates(message.content);
    if (outreachTemplates) {
      console.log(
        "[✅ PARSED] Outreach Templates",
        outreachTemplates.length,
        "templates"
      );
      return (
        <div className="space-y-4">
          {outreachTemplates.map((template, idx) => (
            <CopyableOutputBlock
              key={idx}
              title={template.title}
              description="Ready-to-personalize outreach template"
              content={template.content}
              educationalCallout="Personalize with company research before sending. Replace [bracketed placeholders] with specific details."
            />
          ))}
        </div>
      );
    }
    if (message.content.includes("OUTREACH_TEMPLATES") && !outreachTemplates) {
      console.error(
        "[❌ PARSE FAILED] Outreach Templates markers found but parsing failed"
      );
    }

    // Detect if this SHOULD have been structured output but isn't
    const shouldBeStructured =
      message.content.toLowerCase().includes("optimized resume") ||
      message.content.toLowerCase().includes("gap analysis") ||
      message.content.toLowerCase().includes("cover letter") ||
      message.content.toLowerCase().includes("linkedin profile") ||
      message.content.toLowerCase().includes("interview cheatsheet") ||
      message.content.toLowerCase().includes("practice questions") ||
      message.content.toLowerCase().includes("outreach template") ||
      message.content.includes("Post Idea") ||
      message.content.includes("Template") ||
      message.content.includes("Subject Line:") ||
      (message.content.includes("### ") && message.content.length > 500);

    // Smarter detection: Exclude conversational messages (Step 2 coaching)
    const isConversational =
      (message.content.includes("?") &&
        message.content.split("?").length > 2) || // Multiple questions
      message.content.length < 300 || // Short messages
      [
        "I noticed",
        "Before I",
        "Quick check",
        "To make sure",
        "Let me verify",
        "I need",
        "Can you provide",
        "What's your",
      ].some((phrase) => message.content.startsWith(phrase));

    if (
      shouldBeStructured &&
      !message.content.includes("---") &&
      !isConversational
    ) {
      console.log(
        "[ℹ️ INFO] AI is gathering more context before generating output"
      );
      return (
        <div className="space-y-4">
          <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              💬 <strong>Hey{userName ? ` ${userName}` : ""}!</strong> I need a
              bit more information before creating your personalized content.
              Just answer the questions below, and I'll have everything ready
              for you.
            </p>
          </div>

          <div className="markdown-content space-y-4">
            <ReactMarkdown
              components={{
                h1: ({ node, ...props }) => (
                  <h1
                    className="text-xl font-bold text-foreground mb-3 mt-4"
                    {...props}
                  />
                ),
                h2: ({ node, ...props }) => (
                  <h2
                    className="text-lg font-semibold text-foreground mb-2 mt-4"
                    {...props}
                  />
                ),
                h3: ({ node, ...props }) => (
                  <h3
                    className="text-base font-semibold text-foreground mb-2 mt-3"
                    {...props}
                  />
                ),
                p: ({ node, ...props }) => (
                  <p
                    className="text-sm leading-relaxed text-foreground mb-3"
                    {...props}
                  />
                ),
                ul: ({ node, ...props }) => (
                  <ul
                    className="list-disc list-inside space-y-1 text-sm text-foreground mb-3"
                    {...props}
                  />
                ),
                ol: ({ node, ...props }) => (
                  <ol
                    className="list-decimal list-inside space-y-1 text-sm text-foreground mb-3"
                    {...props}
                  />
                ),
                li: ({ node, ...props }) => (
                  <li className="text-sm leading-relaxed ml-2" {...props} />
                ),
                strong: ({ node, ...props }) => (
                  <strong
                    className="font-semibold text-foreground"
                    {...props}
                  />
                ),
                em: ({ node, ...props }) => (
                  <em className="italic text-foreground" {...props} />
                ),
                code: ({ node, className, children, ...props }) => {
                  const isInline = !className?.includes("language-");
                  return isInline ? (
                    <code
                      className="bg-muted/50 px-1.5 py-0.5 rounded text-xs font-mono text-foreground"
                      {...props}
                    >
                      {children}
                    </code>
                  ) : (
                    <code
                      className="block bg-muted/50 p-3 rounded text-xs font-mono text-foreground mb-3 overflow-x-auto"
                      {...props}
                    >
                      {children}
                    </code>
                  );
                },
                a: ({ node, ...props }) => (
                  <a className="text-primary hover:underline" {...props} />
                ),
                blockquote: ({ node, ...props }) => (
                  <blockquote
                    className="border-l-4 border-primary/30 pl-4 italic text-muted-foreground my-3"
                    {...props}
                  />
                ),
              }}
            >
              {message.content}
            </ReactMarkdown>
          </div>
        </div>
      );
    }

    // Default markdown rendering
    return (
      <div className="markdown-content space-y-4">
        <ReactMarkdown
          components={{
            h1: ({ node, ...props }) => (
              <h1
                className="text-xl font-bold text-foreground mb-3 mt-4"
                {...props}
              />
            ),
            h2: ({ node, ...props }) => (
              <h2
                className="text-lg font-semibold text-foreground mb-2 mt-4"
                {...props}
              />
            ),
            h3: ({ node, ...props }) => (
              <h3
                className="text-base font-semibold text-foreground mb-2 mt-3"
                {...props}
              />
            ),
            p: ({ node, ...props }) => (
              <p
                className="text-sm leading-relaxed text-foreground mb-3"
                {...props}
              />
            ),
            ul: ({ node, ...props }) => (
              <ul
                className="list-disc list-inside space-y-1 text-sm text-foreground mb-3"
                {...props}
              />
            ),
            ol: ({ node, ...props }) => (
              <ol
                className="list-decimal list-inside space-y-1 text-sm text-foreground mb-3"
                {...props}
              />
            ),
            li: ({ node, ...props }) => (
              <li className="text-sm leading-relaxed ml-2" {...props} />
            ),
            strong: ({ node, ...props }) => (
              <strong className="font-semibold text-foreground" {...props} />
            ),
            em: ({ node, ...props }) => (
              <em className="italic text-foreground" {...props} />
            ),
            code: ({ node, className, children, ...props }) => {
              const isInline = !className?.includes("language-");
              return isInline ? (
                <code
                  className="bg-muted/50 px-1.5 py-0.5 rounded text-xs font-mono text-foreground"
                  {...props}
                >
                  {children}
                </code>
              ) : (
                <code
                  className="block bg-muted/50 p-3 rounded text-xs font-mono text-foreground mb-3 overflow-x-auto"
                  {...props}
                >
                  {children}
                </code>
              );
            },
            table: ({ node, ...props }) => (
              <table
                className="w-full border-collapse border border-border mb-3 text-sm"
                {...props}
              />
            ),
            thead: ({ node, ...props }) => (
              <thead className="bg-muted/30" {...props} />
            ),
            tbody: ({ node, ...props }) => <tbody {...props} />,
            tr: ({ node, ...props }) => (
              <tr className="border-b border-border" {...props} />
            ),
            th: ({ node, ...props }) => (
              <th
                className="border border-border px-3 py-2 text-left font-semibold text-foreground"
                {...props}
              />
            ),
            td: ({ node, ...props }) => (
              <td
                className="border border-border px-3 py-2 text-foreground"
                {...props}
              />
            ),
            blockquote: ({ node, ...props }) => (
              <blockquote
                className="border-l-4 border-primary pl-4 italic text-muted-foreground mb-3"
                {...props}
              />
            ),
            a: ({ node, ...props }) => (
              <a
                className="text-primary hover:underline font-medium"
                {...props}
              />
            ),
          }}
        >
          {message.content}
        </ReactMarkdown>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full md:border md:rounded-lg bg-card md:shadow-sm">
      <ScrollArea className="flex-1 p-3 md:p-6" ref={scrollRef}>
        <div className="space-y-3 md:space-y-4">
          {messages.length === 0 && (
            <QuickStartPills onPillClick={handlePillClick} />
          )}
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${
                message.role === "user" ? "justify-end" : "justify-start"
              }`}
            >
              <div
                className={`max-w-[85%] md:max-w-[80%] rounded-2xl px-3 py-2 md:px-4 md:py-3 ${
                  message.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-foreground"
                }`}
              >
                {renderMessage(message)}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
      <div className="flex-shrink-0 border-t bg-background/50 p-2 md:p-4 safe-area-bottom">
        <div className="flex gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Ask about your career goals..."
            className="min-h-[50px] md:min-h-[60px] resize-none rounded-xl text-sm md:text-base"
            disabled={isLoading}
          />
          <Button
            onClick={() => handleSend()}
            disabled={isLoading || !input.trim()}
            size="icon"
            className="h-[50px] w-[50px] md:h-[60px] md:w-[60px] rounded-xl flex-shrink-0"
          >
            <Send className="h-4 w-4 md:h-5 md:w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};
